<template>
	<div :class="classes">
		<div>
			<slot name="icon">
				<FontAwesomeIcon
					v-if="isInfoAlert"
					:icon="[ 'fas', 'info-circle' ]"
					class="teaming-alert__icon info-icon"
				/>
				<FontAwesomeIcon
					v-if="isErrorAlert"
					:icon="[ 'fas', 'exclamation-circle' ]"
					class="teaming-alert__icon error-icon"
				/>
				<FontAwesomeIcon
					v-if="isSuccessAlert"
					:icon="[ 'fas', 'check-circle' ]"
					class="teaming-alert__icon success-icon"
				/>
				<FontAwesomeIcon
					v-if="isWarnAlert"
					:icon="[ `far`, `triangle-exclamation` ]"
					class="teaming-alert__icon warn-icon"
				/>
				<FontAwesomeIcon
					v-if="isSystemAlert"
					:icon="[ `fas`, `message-bot` ]"
					class="teaming-alert__icon system-icon"
				/>
				<TeamingSpinner
					v-if="isPendingAlert"
					:is-spinning="true"
					:is-resetting="false"
				/>
			</slot>
		</div>
		<div class="teaming-alert__text text-default">
			<slot></slot>
		</div>
		<div v-if="$slots.right">
			<slot name="right"></slot>
		</div>
	</div>
</template>

<script>
import {
	Enum,
	AlertType
} from "@teaming/common-enums";

import TeamingSpinner from "./TeamingSpinner.vue";

export const AlertVariant = Enum.create( {
	FILLED: "FILLED",
	HOLLOW: "HOLLOW",
} );

export default {
	name: "TeamingAlert",
	components: {
		TeamingSpinner,
	},
	props: {
		type: {
			type: String,
			default: AlertType.INFO,
			validator( type ) {
				return AlertType.includes( type );
			},
		},
		variant: {
			type: String,
			default: AlertVariant.FILLED,
		},
	},
	computed: {
		classes() {
			return {
				"teaming-alert": true,
				"teaming-alert--info": ( this.isInfoAlert || this.isPendingAlert ),
				"teaming-alert--error": this.isErrorAlert,
				"teaming-alert--success": this.isSuccessAlert,
				"teaming-alert--warn": this.isWarnAlert,
				"teaming-alert--system": this.isSystemAlert,
				hollow: ( this.variant === AlertVariant.HOLLOW ),
			};
		},
		isInfoAlert() {
			return this.type === AlertType.INFO;
		},
		isErrorAlert() {
			return this.type === AlertType.ERROR;
		},
		isSuccessAlert() {
			return this.type === AlertType.SUCCESS;
		},
		isPendingAlert() {
			return this.type === AlertType.PENDING;
		},
		isWarnAlert() {
			return this.type === AlertType.WARN;
		},
		isSystemAlert() {
			return this.type === AlertType.SYSTEM;
		},
	},
};
</script>

<style scoped lang="scss">
$teaming-alert-info-background-color: $color2-100;
$teaming-alert-info-text-color: $color2-700;
$teaming-alert-info-icon-color: $color2-500;
$teaming-alert-info-border-color--hollow: $color2-300;
$teaming-alert-info-background-color--hollow: $color2-050;

$teaming-alert-error-background-color: $color7-100;
$teaming-alert-error-text-color: $color7-700;
$teaming-alert-error-icon-color: $color7-500;
$teaming-alert-error-border-color--hollow: $color7-300;
$teaming-alert-error-background-color--hollow: $color7-050;

$teaming-alert-success-background-color: $color4-100;
$teaming-alert-success-text-color: $color4-700;
$teaming-alert-success-icon-color: $color4-500;
$teaming-alert-success-border-color--hollow: $color4-300;
$teaming-alert-success-background-color--hollow: $color4-050;

$teaming-alert-warn-background-color: $color5-100;
$teaming-alert-warn-text-color: $color5-700;
$teaming-alert-warn-icon-color: $color5-500;
$teaming-alert-warn-border-color--hollow: $color5-300;
$teaming-alert-warn-background-color--hollow: $color5-050;

$teaming-alert-system-background-color: $color9-100;
$teaming-alert-system-text-color: $color9-700;
$teaming-alert-system-icon-color: $color9-500;
$teaming-alert-system-border-color--hollow: $color9-300;
$teaming-alert-system-background-color--hollow: $color9-050;

.teaming-alert {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 0.5rem;

	width: 100%;
	padding: 0.5rem;
	border-radius: 0.5rem;
	border: 0.0625rem solid transparent;

	& > :first-child {
		padding-top: 0.1rem;
	}

	& > :nth-child(2) {
		flex: 1;
	}
}

.teaming-alert--info {
	background-color: $teaming-alert-info-background-color;
	color: $teaming-alert-info-text-color;
	.teaming-alert__icon {
		color: $teaming-alert-info-icon-color;
	}
}

.teaming-alert--info.hollow {
	background-color: $teaming-alert-info-background-color--hollow;
	border-color: $teaming-alert-info-border-color--hollow;
}

.teaming-alert--error {
	background-color: $teaming-alert-error-background-color;
	color: $teaming-alert-error-text-color;
	.teaming-alert__icon {
		color: $teaming-alert-error-icon-color;
	}
}

.teaming-alert--error.hollow {
	background-color: $teaming-alert-error-background-color--hollow;
	border-color: $teaming-alert-error-border-color--hollow;
}

.teaming-alert--success {
	background-color: $teaming-alert-success-background-color;
	color: $teaming-alert-success-text-color;
	.teaming-alert__icon {
		color: $teaming-alert-success-icon-color;
	}
}

.teaming-alert--success.hollow {
	background-color: $teaming-alert-success-background-color--hollow;
	border-color: $teaming-alert-success-border-color--hollow;
}

.teaming-alert--warn {
	background-color: $teaming-alert-warn-background-color;
	color: $teaming-alert-warn-text-color;
	.teaming-alert__icon {
		color: $teaming-alert-warn-icon-color;
	}
}

.teaming-alert--warn.hollow {
	background-color: $teaming-alert-warn-background-color--hollow;
	border-color: $teaming-alert-warn-border-color--hollow;
}

.teaming-alert--system {
	background-color: $teaming-alert-system-background-color;
	color: $teaming-alert-system-text-color;
	.teaming-alert__icon {
		color: $teaming-alert-system-icon-color;
	}
}

.teaming-alert--system.hollow {
	background-color: $teaming-alert-system-background-color--hollow;
	border-color: $teaming-alert-system-border-color--hollow;
}
</style>
